import BiteClassification from 'components/Patients/BiteClassification/BiteClassification'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import LinkButton from 'components/common/buttons/LinkButton'
import SecondaryButton from 'components/common/buttons/SecondaryButton'
import ConfirmationModal from 'components/common/ConfirmationModal'
import { Close1 } from 'components/common/icons'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { TreatmentTrackOptions } from 'consts/giConsts'
import useRolePermissions from 'hooks/useRolePermissions'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { sortTeeth } from 'utils/giUtils'
import AINotifyPractice from './AINotifyPractice/AINotifyPractice'
import ConditionDetails from './ConditionDetails'
import EditTrackingTagging from './EditTrackingTagging'
import RelevantPracticeGuidelines from './RelevantPracticeGuidelines/RelevantPracticeGuidelines'
import useRelevantPracticeGuidelines from './RelevantPracticeGuidelines/useRelevantPracticeGuidelines'
import TrackButton from './TrackButton'
import useTreatmentTracking from './useTreatmentTracking'
import useGaFlags from 'hooks/useGaFlags'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative'
  },
  disabledOverlay: {
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    position: 'absolute',
    background: '#fff',
    opacity: 0.7,
    zIndex: 9999
  },
  editButton: {
    userSelect: 'none',
    marginLeft: 20
  }
}))

const TreatmentTrackingTagging = ({ scanId }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { AIForHI } = useGaFlags()

  const { status } = useSelector(state => state.giReducer.scanTracking)
  const { isHiUser } = useRolePermissions()

  const [isEditing, setIsEditing] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)

  const {
    biteClassificationEnabled,
    biteFlagError,
    isSubmitDisabled,
    isLoading,
    values,
    conditionOptions,
    irrelevantConditionOptions,
    untrackedTeeth,
    isNoteEnabled,
    selectedConditionDetails,
    setConditionEnabled,
    setConditionSelections,
    submit,
    resetChanges,
    dismiss
  } = useTreatmentTracking({
    scanId
  })

  const { categories, filteredCategoriesByTrackingDetails } = useRelevantPracticeGuidelines({
    trackingDetails: values.conditions
  })

  const handleConfirm = useCallback(() => {
    dismiss()
    setConfirmationOpen(false)
  }, [dismiss])

  const handleToggleEditing = useCallback(() => {
    trackEvent('Treatment Tracking - Edit clicked', { isEditing: true })
    setIsEditing(true)
  }, [])

  const handleCancelChanges = useCallback(() => {
    trackEvent('Treatment Tracking - Changes discarded', {
      isEditing: false
    })
    setIsEditing(false)
    resetChanges()
  }, [resetChanges])

  const handleSetTrackStatusClicked = useCallback(
    value => {
      trackEvent('Treatment tracking tagging - Track status set', {
        status: value
      })
      values.setTrackStatus(value)
      setIsEditing(true)
    },
    [values]
  )

  useEffect(() => {
    if (status === 'completed' && isEditing) {
      setIsEditing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  return (
    <>
      <Grid container direction="column" spacing={2} className={classes.root}>
        <Grid item>
          <DazzedParagraph14 bold>
            {t('pages.patients.selectedPatient.chat.gi.treatmentTracking.title')}
          </DazzedParagraph14>
        </Grid>
        {untrackedTeeth?.length > 0 && (
          <Grid item>
            <DazzedParagraph12>
              {t('pages.patients.selectedPatient.chat.gi.untrackedTeeth', {
                teeth: sortTeeth(untrackedTeeth).join(', ')
              })}
            </DazzedParagraph12>
          </Grid>
        )}
        <Grid item>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <TrackButton
                value={TreatmentTrackOptions.OnTrack}
                onClick={handleSetTrackStatusClicked}
                isSelected={values.trackStatus === TreatmentTrackOptions.OnTrack}
              />
            </Grid>
            <Grid item>
              <TrackButton
                value={TreatmentTrackOptions.OffTrack}
                onClick={handleSetTrackStatusClicked}
                isSelected={values.trackStatus === TreatmentTrackOptions.OffTrack}
              />
            </Grid>
            {isHiUser && (
              <Grid item>
                <SecondaryButton
                  label={t('general.dismiss')}
                  onClick={() => setConfirmationOpen(true)}
                  small
                  isLoading={isLoading}
                />
              </Grid>
            )}
            <Grid item className={classes.editButton}>
              <LinkButton
                icon={isEditing ? <Close1 /> : undefined}
                underline
                fontSize={14}
                label={isEditing ? t('general.cancel') : t('pages.patients.selectedPatient.chat.gi.edit')}
                onClick={isEditing ? handleCancelChanges : handleToggleEditing}
              />
            </Grid>
          </Grid>
        </Grid>
        {biteFlagError && <Grid item>{t('biteClassification.loadError')}</Grid>}
        {biteClassificationEnabled && (
          <Grid item>
            <BiteClassification
              value={values.biteClassification}
              onChange={isEditing ? values.setBiteClassification : undefined}
            />
          </Grid>
        )}
        {isEditing ? (
          <EditTrackingTagging
            conditionOptions={conditionOptions}
            setConditionEnabled={setConditionEnabled}
            values={values}
            setConditionSelections={setConditionSelections}
            untrackedTeeth={untrackedTeeth}
            isNoteEnabled={isNoteEnabled}
            submit={submit}
            isLoading={isLoading}
            isSubmitDisabled={isSubmitDisabled}
            isHiUser={isHiUser}
            irrelevantConditionOptions={irrelevantConditionOptions}
          />
        ) : (
          selectedConditionDetails.length > 0 && (
            <Grid item>
              <Grid container alignItems="center" spacing={1}>
                {selectedConditionDetails.map((conditionDetails, index) => (
                  <Grid item key={`condition-string-${index}`}>
                    <ConditionDetails conditionDetails={conditionDetails} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )
        )}
        {AIForHI && !isEditing && values.trackStatus && (
          <>
            <Grid item>
              <RelevantPracticeGuidelines
                categories={categories}
                filteredCategoriesByTrackingDetails={filteredCategoriesByTrackingDetails}
              />
            </Grid>
            <Grid item>
              <AINotifyPractice
                filteredCategoriesByTrackingDetails={filteredCategoriesByTrackingDetails}
                selectedConditionDetails={selectedConditionDetails}
              />
            </Grid>
          </>
        )}
      </Grid>
      <ConfirmationModal
        isOpen={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirm}
        title={t('dialogs.scanSummary.treatmentTracking.dismissConfirmation.title')}
        acceptButtonText={t('general.dismiss')}
      />
    </>
  )
}

export default TreatmentTrackingTagging
